/*
defines the main data structures of our application, 
and provides functionality for updating the data
*/

import Link from './link';
import ImageNode from './imageNode';
import WikiNode from './wikiNode';
import ConnectorNode from './connectorNode';
import LocalImageNode from './localImageNode';
import {removeItem, saveDoc, importDoc} from '../utilities/utilities';

/**
 * defines a structure for storing the network of nodes
 */
export default class Graph {
    /**
     * makes a new graph given a list of nodes and links
     * @param {Array<Node>} [nodes] (optional)
     * @param {Array<Link>} [links] (optional)
     */
    constructor(nodes = [], links = []) {
        this.nodes = nodes;
        this.links = links;
    }

    /**
     * makes and adds a new node to this graph
     * @param {string} type
     * @param {Object} [apiSource] (optional) the api the new node is using
     * @param {Object} [apiResponse] (optional) the api response
     * @param {Function} [updateCallback] (optional) the function to be called when the API is updated
     * @return {Node | null}
     */
    addNode(type, apiSource = {}, apiResponse = {}, updateCallback = function() {}) {
        let id = (Math.floor(Math.random() * 100000)).toString();
        let newNode;
        if (type === 'image') {
            newNode = new ImageNode(id, updateCallback, apiSource, apiResponse);
        } else if (type === 'wiki') {
            newNode = new WikiNode(id, updateCallback, apiSource, apiResponse, apiSource.name, apiSource.style);
        } else if (type === 'connector') {
            newNode = new ConnectorNode(id);
        }
        if (newNode) {
            this.nodes.push(newNode);
            return newNode;
        } else {
            console.error('no node added');
            return null;
        }
    }

    /**
     * makes a new image node from a locally uploaded image
     * @example
     *  addLocalImageNode({
     *      width: 160,
     *      height: 160,
     *      url: 'http://placekitten.com/g/160/160'
     *  })
     * @param {Object} imageInfo an object of the form { url, width, height }
     * @return {LocalImageNode}
     */
    addLocalImageNode(imageInfo) {
        let id = Math.floor(Math.random() * 100000).toString();
        let newNode = new LocalImageNode(id, imageInfo);
        this.nodes.push(newNode);
        return newNode;
    }

    /**
     * makes a new connector node given some text
     * @param {string} text
     * @return {ConnectorNode}
     */
    addConnectorNode(text) {
        let id = Math.floor(Math.random() * 100000).toString();
        let newNode = new ConnectorNode(id, text);
        this.nodes.push(newNode);
        return newNode;
    }

    /**
     * adds a new link to the graph which connects
     * the nodes with the given indices
     * @param {string} type the type of link to create (none currently)
     * @param {number} source the id of the first node to connect
     * @param {number} target the id of the second node to connect
     * @return {Link}
     */
    addLink(type, source, target) {
        if (!this.linkExists(source, target)) {
            let newLink = new Link(type, source, target, Math.floor(Math.random() * 100000).toString());
            this.links.push(newLink);
            return newLink;
        } else {
            return this.getLinks(source, target)[0];
        }
    }

    /**
     * empty the graph model
     */
    clearGraph() {
        this.nodes = [];
        this.links = [];
    }

    /**
     * checks if a link exists between the given source and target IDs
     * @param {string} source
     * @param {string} target
     * @return {boolean}
     */
    linkExists(source, target) {
        return this.links.some((link) => {
            if ((link.source == source && link.target == target) ||
                (link.source == target && link.target == source)) {
                return true;
            }
            return false;
        });
    }

    /**
     * returns all the links connecting the two given nodes
     * @param {string} source the id of the first node
     * @param {string} target the id of the second node
     * @return {Link[]}
     */
    getLinks(source, target) {
        let foundLinks = [];
        this.links.forEach((link) => {
            if ((link.source == source && link.target == target) ||
                (link.source == target && link.target == source)) {
                foundLinks.push(link);
            }
        });
        return foundLinks;
    }

    /**
     * returns all the links connecting the the given node
     * @param {string} source the id of the first node
     * @return {Link[]}
     */
    getLinksToNode(source) {
        let foundLinks = [];
        this.links.forEach((link) => {
            if (link.source === source || link.target === source) {
                foundLinks.push(link);
            }
        });
        return foundLinks;
    }

    /**
     * removes the link given its id
     * Problem: confusion between id / id when nodes are deleted
     * @param {string} id the id of the link to be removed
     */
    removeLink(id) {
        removeItem(this.links, id);
    }


    /**
     * removes the node given its id
     * Problem: confusion between id / id when nodes are deleted
     * @param {string} id the id of the node to be removed
     */
    removeNode(id) {
        removeItem(this.nodes, id);
        _.map(this.getLinksToNode(id), (link) => this.removeLink(link.id));
    }

    /**
     * gets this graph model in an exportable JSON format
     * @return {{nodes: Node[], links: Link[]}}
     */
    toJSON() {
        return {
            nodes: this.nodes.map((node, idx, array) => {
                let obj = {
                    id: node.id,
                    type: node.type,
                    name: 'DB',
                };

                if (node.type === 'image') {
                    obj.apiResponse = node.apiResponse;
                    obj.image = node.image;
                    obj.style = node.apiSource.style ? node.apiSource.style : 'null';
                    obj.source = node.apiSource.name ? node.apiSource.name : 'null';
                    // obj.apiSource.image = node.apiSource.image;
                } else if (node.type === 'wiki' || node.type === 'connector') {
                    obj.text = node.text;
                    obj.apiResponse = node.apiResponse;
                    obj.source = node.apiSource.name ? node.apiSource.name : 'null';
                    obj.style = node.apiSource.style ? node.apiSource.style : node.style ? node.style : null;
                    obj.query = obj.style === 'wiktionary' ? node.apiSource.getQuery() : null;
                }

                return obj;
            }),
            links: this.links.map((link, idx, array) => {
                let obj = {
                    id: link.id,
                    source: link.source,
                    target: link.target,
                    type: link.type,
                    strength: link.strength,
                    distance: link.distance,
                };

                return obj;
            })
        };
    }

    /**
     * imports a graph json into this model using
     * the importDoc function in utilities
     */
    loadFromDB() {
        importDoc().then((elements) => {
            this.nodes = elements.nodes;
            this.links = elements.links;
        });
    }

    /**
     * backs up this model to firebase using the saveDoc function in utilities
     */
    backup() {
        let exp = this.toJSON();
        saveDoc({model: exp});
    }
}